import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProjects } from '../../contexts/ProjectContext';
import toast, { Toaster } from 'react-hot-toast';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import UploadedImageThumb from '../../components/UploadedImageThumb';
import AssetLibraryPanel from '../../components/AssetLibraryPanel';
import PromptTaskBar from './components/PromptTaskBar';
import PromptTaskBarMobile from './components/PromptTaskBarMobile';
import GenerationSettings from './components/GenerationSettings';
import GenerationSettingsMobile from './components/GenerationSettingsMobile';
import { generateSiteConcept } from '../../services/geminiService';
import { generateWithFlux, isFluxModel } from '../../services/fluxService';
import resolutionMap from '../../constants/resolutionMap';
import { setGeneratedImageDragData } from '../../utils/dragDropUtils';
import { useAuth } from '../../contexts/AuthContext';
import {
  projectsService,
  savedDesignsService,
  generationMetadataService,
  userProfilesService,
  storageService,
} from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import GenerationStatusNotification from '../../components/GenerationStatusNotification';
import useGenerationTimeout from '../../hooks/useGenerationTimeout';
import { getModelCreditCost } from '../../utils/creditCost';
import { checkSaveDesignLimit } from '../../utils/planLimits';
import { downloadImage } from '../../utils/downloadUtils';

const SiteConcept = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSaveModal } = useProjects();
  const { user, userProfile, loading, profileLoading } = useAuth();

  // Virtual Tool Guide state
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(1);
  const [guideAnimating, setGuideAnimating] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);
  const guideStepCount = 11;

  // Guide refs — desktop
  const siteUploadDesktopRef = useRef(null);
  const refBuildingDesktopRef = useRef(null);
  const genSettingsDesktopRef = useRef(null);
  const assetLibraryDesktopRef = useRef(null);
  const resultDesktopRef = useRef(null);
  const promptDesktopRef = useRef(null);
  const angleDesktopRef = useRef(null);
  const modelDesktopRef = useRef(null);
  const aspectRatioGuideDesktopRef = useRef(null);
  const resolutionGuideDesktopRef = useRef(null);
  const generateBtnDesktopRef = useRef(null);

  // Guide refs — mobile
  const siteUploadMobileRef = useRef(null);
  const refBuildingMobileRef = useRef(null);
  const genSettingsMobileRef = useRef(null);
  const assetLibraryMobileRef = useRef(null);
  const resultMobileRef = useRef(null);
  const promptMobileRef = useRef(null);
  const angleMobileRef = useRef(null);
  const modelMobileRef = useRef(null);
  const aspectRatioGuideMobileRef = useRef(null);
  const resolutionGuideMobileRef = useRef(null);
  const generateBtnMobileRef = useRef(null);

  const openGuide = () => {
    setGuideStep(1);
    setShowGuide(true);
    setGuideVisible(false);
    const ref = window.innerWidth >= 768 ? siteUploadDesktopRef.current : siteUploadMobileRef.current;
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
      }, 500);
    } else {
      requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
    }
  };

  const nextGuideStep = () => {
    if (guideAnimating) return;
    setGuideAnimating(true);
    setGuideVisible(false);
    const nextStep = guideStep + 1;
    const needsScroll = nextStep === 3 || nextStep === 4 || nextStep === 5 || nextStep === 11;
    if (needsScroll) {
      const desktopRefsList = [siteUploadDesktopRef, refBuildingDesktopRef, genSettingsDesktopRef, assetLibraryDesktopRef, promptDesktopRef, angleDesktopRef, modelDesktopRef, aspectRatioGuideDesktopRef, resolutionGuideDesktopRef, generateBtnDesktopRef, resultDesktopRef];
      const mobileRefsList = [siteUploadMobileRef, refBuildingMobileRef, genSettingsMobileRef, assetLibraryMobileRef, promptMobileRef, angleMobileRef, modelMobileRef, aspectRatioGuideMobileRef, resolutionGuideMobileRef, generateBtnMobileRef, resultMobileRef];
      const ref = window.innerWidth >= 768 ? desktopRefsList[nextStep - 1]?.current : mobileRefsList[nextStep - 1]?.current;
      if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setTimeout(() => {
      setGuideStep(nextStep);
      setGuideAnimating(false);
      if (needsScroll) {
        setTimeout(() => {
          requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
        }, 400);
      } else {
        requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
      }
    }, 600);
  };

  const closeGuide = () => {
    setGuideVisible(false);
    setTimeout(() => {
      setShowGuide(false);
      setGuideStep(1);
    }, 600);
  };

  const [libraryAssets, setLibraryAssets] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const abortControllerRef = useRef(null);
  const isGeneratingRef = useRef(false);
  const [generationError, setGenerationError] = useState('');
  const [lastGenerationParams, setLastGenerationParams] = useState(null);
  const { generationStatus, startTimer, clearTimer, setFailed, setCancelled, dismissNotification } = useGenerationTimeout();

  // Restored prompt/model values (passed to PromptTaskBar as initial values)
  const [restoredPrompt, setRestoredPrompt] = useState('');
  const [restoredModel, setRestoredModel] = useState('');
  const [restoredImageSize, setRestoredImageSize] = useState('');

  // Upload states
  const [siteImages, setSiteImages] = useState([]);
  const [referenceImages, setReferenceImages] = useState([]);

  // Generation
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [sessionLibraryImages, setSessionLibraryImages] = useState([]);

  // Generation settings
  const [generationSettings, setGenerationSettings] = useState({
    floors: '',
    lighting: '',
    buildingType: '',
    surrounding: '',
    season: '',
    sky: '',
  });

  const totalUploads = siteImages.length + referenceImages.length;

  const handleSiteUpload = async (e) => {
    const files = Array.from(e?.target?.files || []);
    const totalCurrent = siteImages.length + referenceImages.length;
    const remaining = Math.max(0, 6 - totalCurrent);
    if (remaining === 0) return;
    const limitedFiles = files.slice(0, remaining);
    const urls = (await Promise.all(limitedFiles.map((f) => storageService.uploadFile(f, user?.id)))).filter(Boolean);
    setSiteImages((prev) => [...prev, ...urls]);
  };

  const handleReferenceUpload = async (e) => {
    const files = Array.from(e?.target?.files || []);
    const totalCurrent = siteImages.length + referenceImages.length;
    const remaining = Math.max(0, 6 - totalCurrent);
    if (remaining === 0) return;
    const limitedFiles = files.slice(0, remaining);
    const urls = (await Promise.all(limitedFiles.map((f) => storageService.uploadFile(f, user?.id)))).filter(Boolean);
    setReferenceImages((prev) => [...prev, ...urls]);
  };

  const handleRemoveSiteImage = (idx) => {
    setSiteImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveReferenceImage = (idx) => {
    setReferenceImages((prev) => prev.filter((_, i) => i !== idx));
  };

  // Restore project data when opened from /project-history
  useEffect(() => {
    const projectId = location.state?.projectId;
    if (!projectId) return;
    const loadProjectData = async () => {
      setCurrentProjectId(projectId);
      const designs = await savedDesignsService.getDesigns(projectId);
      if (designs && designs.length > 0) {
        const imageUrls = designs.map((d) => d.imageUrl).filter(Boolean);
        if (imageUrls.length > 0) setSessionLibraryImages(imageUrls);
        setGeneratedImage(designs[0].imageUrl);
        if (designs[0]?.prompt) setRestoredPrompt(designs[0].prompt);

        // Restore generation settings from metadata
        try {
          const metadata = await generationMetadataService.getMetadata(designs[0].id);
          if (metadata) {
            const extra = metadata.additionalParams || {};
            setGenerationSettings((prev) => ({
              ...prev,
              lighting: metadata.lighting && metadata.lighting !== 'Auto' ? metadata.lighting : prev.lighting,
              floors: extra.floors || prev.floors,
              buildingType: extra.buildingType || prev.buildingType,
              surrounding: extra.surrounding || prev.surrounding,
              season: extra.season || prev.season,
              sky: extra.sky || prev.sky,
            }));
            if (metadata.modelUsed) setRestoredModel(metadata.modelUsed);
            if (metadata.imageSize) setRestoredImageSize(metadata.imageSize);
            if (!designs[0]?.prompt && extra.prompt) setRestoredPrompt(extra.prompt);
            if (metadata?.usedMedia?.length > 0) setLibraryAssets(metadata.usedMedia);
          }
        } catch (metaErr) {
          console.error('Error loading metadata:', metaErr);
        }
      }
    };
    loadProjectData();
  }, []);


  // Safe drag-drop handlers that use refs to avoid stale closure on totalUploads
  const siteImagesRef = useRef(siteImages);
  const referenceImagesRef = useRef(referenceImages);
  useEffect(() => { siteImagesRef.current = siteImages; }, [siteImages]);
  useEffect(() => { referenceImagesRef.current = referenceImages; }, [referenceImages]);

  const getAvailableSlots = useCallback(() => {
    return Math.max(0, 6 - siteImagesRef.current.length - referenceImagesRef.current.length);
  }, []);

  const handleSiteDrop = useCallback(async (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-orange-500/50');
    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
    if (imageUrl) {
      if (getAvailableSlots() > 0) {
        setSiteImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      }
      return;
    }
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
      const remaining = getAvailableSlots();
      const limited = files.slice(0, remaining);
      const urls = (await Promise.all(limited.map(f => storageService.uploadFile(f, user?.id)))).filter(Boolean);
      setSiteImages(prev => [...prev, ...urls]);
    }
  }, [getAvailableSlots, user?.id]);

  const handleReferenceDrop = useCallback(async (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-orange-500/50');
    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
    if (imageUrl) {
      if (getAvailableSlots() > 0) {
        setReferenceImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      }
      return;
    }
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
      const remaining = getAvailableSlots();
      const limited = files.slice(0, remaining);
      const urls = (await Promise.all(limited.map(f => storageService.uploadFile(f, user?.id)))).filter(Boolean);
      setReferenceImages(prev => [...prev, ...urls]);
    }
  }, [getAvailableSlots, user?.id]);

  const handleUploadDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    e.currentTarget.classList.add('border-orange-500/50');
  }, []);

  const handleUploadDragLeave = useCallback((e) => {
    e.currentTarget.classList.remove('border-orange-500/50');
  }, []);

  const handleDeleteResult = () => {
    if (generatedImage && generatedImage.startsWith('blob:')) {
      URL.revokeObjectURL(generatedImage);
    }
    setGeneratedImage(null);
    setIsGenerating(false);
    setGenerationError('');
  };

  const handleDownload = async () => {
    if (!generatedImage) return;
    await downloadImage(generatedImage, `site-concept-${Date.now()}.png`);
  };

  const handleRegenerate = async () => {
    if (!generatedImage) return;
    setGenerationError('');
    // Always use the most recent generation params so Regenerate respects current settings
    await handleGenerate(lastGenerationParams);
  };

  // Resize image to correct aspect ratio
  const resizeImageToCorrectDimensions = async (imageUrl, targetDimensions) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.crossOrigin = 'anonymous';
      img.onload = () => {
        canvas.width = targetDimensions.width;
        canvas.height = targetDimensions.height;

        const imgAspect = img.width / img.height;
        const canvasAspect = canvas.width / canvas.height;

        let drawWidth, drawHeight, drawX, drawY;

        if (imgAspect > canvasAspect) {
          drawHeight = canvas.height;
          drawWidth = drawHeight * imgAspect;
          drawX = (canvas.width - drawWidth) / 2;
          drawY = 0;
        } else {
          drawWidth = canvas.width;
          drawHeight = drawWidth / imgAspect;
          drawX = 0;
          drawY = (canvas.height - drawHeight) / 2;
        }

        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

        canvas.toBlob(
          async (blob) => {
            try {
              const reader = new FileReader();
              reader.onloadend = async () => {
                const uploadedUrl = await storageService.uploadImage(reader.result, user?.id);
                resolve(uploadedUrl || URL.createObjectURL(blob));
              };
              reader.readAsDataURL(blob);
            } catch {
              resolve(URL.createObjectURL(blob));
            }
          },
          'image/png',
        );
      };

      img.onerror = () => reject(new Error('Failed to load image for resizing'));
      img.src = imageUrl;
    });
  };

  // Get image dimensions based on selected aspect ratio
  const getImageDimensions = (aspectRatio, resolution) => {
    const ratioMap = resolutionMap[aspectRatio];
    if (ratioMap && ratioMap[resolution]) {
      return ratioMap[resolution];
    }
    const defaultDimensions = {
      'Auto': { width: 1024, height: 1024 },
      '1:1': { width: 2048, height: 2048 },
      '4:3': { width: 2048, height: 1536 },
      '3:2': { width: 2048, height: 1365 },
      '16:9': { width: 2048, height: 1152 },
      '21:9': { width: 2048, height: 878 },
      '5:4': { width: 2048, height: 1638 },
      '2:3': { width: 1365, height: 2048 },
      '9:16': { width: 1152, height: 2048 },
      '3:4': { width: 1536, height: 2048 },
      '4:5': { width: 1638, height: 2048 }
    };
    return defaultDimensions[aspectRatio] || { width: 1024, height: 1024 };
  };

  const handleGenerate = async (params) => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    // Validate required inputs
    if (!siteImages || siteImages.length === 0) {
      toast.error('Site image is required. Please upload at least one site image.');
      isGeneratingRef.current = false;
      return;
    }

    if (!referenceImages || referenceImages.length === 0) {
      toast.error('Reference building is required. Please upload at least one building image.');
      isGeneratingRef.current = false;
      return;
    }

    // Auth check
    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    if (!session || !session?.user?.id) {
      toast.error('Please sign in to generate images.');
      isGeneratingRef.current = false;
      return;
    }
    const currentUser = session?.user;

    // Credit check (supports team members sharing owner's pool)
    const creditCost = getModelCreditCost(params?.selectedModel || 'Gemini 3.1 Flash Image Preview');
    const creditCheck = await userProfilesService?.checkCredits(currentUser?.id, creditCost);
    if (!creditCheck?.allowed) {
      toast.error(creditCheck?.message || 'Insufficient credits.');
      isGeneratingRef.current = false;
      return;
    }

    // Check storage limit
    {
      const { allowed, current, max } = await checkSaveDesignLimit(currentUser?.id, userProfile?.subscription_tier);
      if (!allowed) {
        toast.error(`Storage limit reached (${current}/${max}). Please upgrade your plan or delete existing files.`);
        isGeneratingRef.current = false;
        return;
      }
    }

    setIsGenerating(true);
    startTimer();
    setGeneratedImage(null);
    setGenerationError('');
    abortControllerRef.current = new AbortController();

    try {
      const dimensions = getImageDimensions(params?.selectedAspectRatio || '1:1', params?.selectedResolution || '1K');

      // Site-concept specific system prompt
      const siteConceptSystemPrompt = `Role: You are a neutral translator between interior design platform user inputs and Gemini 3.1 Flash Image Preview image generation model for space planning visualization. Task for you: integrate naturally and cohesively the building / house or any exterior physical building onto the reference land (or site) uploaded by user. maintain maximum possible consistency to the reference materials of site and the building provided by user, try to preserve their original look. however, prioritize solving the challenge of harmonizing two objects together into one fulfilled and truthfully looking generated image, hyper realism style. before generating, analyze the land user provided, if there are trees, rocks, pavement or any surface which influences your decision of how to integrate the house or building into overall composition. Keywords and direction to tune up generated image: integrate naturally and cohesively the building onto the reference land, so that they both seem like one unified layer with the same color correction, space 3D volume and shades. if the images have different styles or the rendering style for building (raw), prioritize the hyper real style, like shot on professional camera, with shadows, volume, 3D perspective and natural integration of the building into scene, so it looks highly believable and real. adjust the composition (e.g. if the building is extreme wide shot, make it truthful on medium shot land, find balance between their sizes and adjust them into one harmonized size, maintaining maximum possible similarity to their original look) blend these layers together so that smooth uniform composition is generated. analyze the dimension and make generation based on dimensions analysis, so that the architecture of buiding logically and correctly corresponds to the land dimension, perimeter, inclinations, and vice versa. so that there are no logical visual errors in dimensions of building placement, according to geometrical rules. pay high attention to analysis of 3 dimensions , volume of the image. The camera angle must be chosen rightly. every element must be carefully harmonized all together: camera angle shot, 3D-volume of space, unification of different layers, make the building more realistic. another changes which you can do comes from the prompt of a user, expressed will of a user, e.g. different angle or the variables inside interface user chooses. pay careful attention to the fields which user filled in like Number of Floors, Lighting Condition, Building Type, Surrounding Context, Season, Sky Condition -- these are the keys for you to create the compelling generation for user.
Send all image references and text inputs uploaded by the user directly to Gemini API in one single call. Apply the judgment of a 10-year professional architectural visualizer — someone who understands that a convincing site composite requires the building to feel monumental, grounded, and dimensionally truthful, not like a scale model placed on a photograph.
DETAILED EXECUTION STEPS — Before generating, perform the following analysis steps in order:
Step 1 — Analyse the site image. Identify the terrain characteristics: ground elevation, slope, inclinations, existing vegetation (trees, shrubs, grass), rock formations, soil type, access roads, neighbouring structures, horizon line, camera angle, lens perspective (wide angle, telephoto, eye-level), natural light direction, time of day, weather conditions, and the overall dimensional scale of the land parcel. These characteristics define the spatial context into which the building must be integrated.
Step 2 — Analyse the building reference image. Identify the architectural form: building height, number of floors, façade materials, structural rhythm, roof profile, entrance orientation, and overall massing. Determine the building's dimensional proportions relative to human scale (floor height ≈ 3–3.5 metres, entrance doors ≈ 2.1 metres). Classify the building by height category: Low-rise (1–4 floors, up to ~15 metres): houses, villas, small commercial buildings. Mid-rise (5–12 floors, ~15–45 metres): apartment buildings, office blocks, hotels. High-rise / Skyscraper (13+ floors, 45+ metres): towers, skyscrapers, supertall structures. This classification determines how the integration must be approached — each category has fundamentally different spatial, compositional, and grounding requirements.
Step 3 — Camera and scale compatibility analysis. This is the most critical step for achieving convincing integration. Compare the site's camera position with what the building requires to look dimensionally truthful: For low-rise buildings (1–4 floors): The site's natural camera angle typically works. The building can be placed at a moderate distance and still appear at realistic scale. Minor adjustments are usually sufficient. For mid-rise buildings (5–12 floors): The building should occupy a substantial portion of the image height. If the site's camera angle is too distant or too wide, the virtual camera position must be adjusted — bring the viewpoint closer to where the building stands so the building fills an appropriate vertical proportion of the frame. For high-rise buildings and skyscrapers (13+ floors): This is where integration most commonly fails. A skyscraper photographed from 500 metres away on a wide-angle landscape shot will look like a toy model. To prevent this: The building must dominate the frame vertically — a 30-floor tower should occupy 60–80% of the image height. The virtual camera must be positioned as if the photographer is standing close to the building (within 50–150 metres for very tall structures), looking slightly upward. The site's landscape may be reframed — showing less horizontal expanse and more vertical space — so the building reads at its true monumental scale. Perspective foreshortening must be applied: the building's base appears wider and the upper floors converge toward the top, as seen in real architectural photography of tall structures. The site's horizon line, distant features, and sky must adapt to this closer camera position. The ground plane in the foreground should show more detail (pavement texture, ground-level landscaping, arrival areas) because the camera is closer. The core principle: The camera must serve the building's scale, not the other way around. If the site photo was taken from far away but the building is a 40-story tower, the output must reframe the composition so the building feels monumental. The site's general character (terrain type, vegetation style, sky, background elements) is preserved, but the camera distance, framing, and ground-level detail may be creatively adjusted to make the building look like it was photographed on location by an architectural photographer who chose the right lens and distance to make the building look impressive.
Step 4 — Ground-level integration analysis. Every building needs a believable transition between its base and the terrain. The complexity of this transition scales with building height: Low-rise buildings: Natural ground contact — the foundation meets the terrain with minimal modification. A small driveway, pathway, or garden bed at the base is sufficient. Residential landscaping (lawn, flower beds, a path to the entrance) creates natural integration. Mid-rise buildings: Require a more developed ground plane — a small parking area, pedestrian walkways, entrance canopy, ground-level landscaping with hedges or planters. The building's base should show the commercial or residential entrance context appropriate to its type. High-rise buildings and skyscrapers: Require substantial ground-level infrastructure to look convincing. A skyscraper does not simply grow out of raw dirt. The building's base must include contextually appropriate elements: A podium, plaza, or hardscaped arrival area at the base. Pedestrian-scale elements near the ground: entrance canopy, lobby glazing, bollards, signage, lighting fixtures. Transitional landscaping between the building's hardscape and the site's natural terrain — a gradual shift from paved areas near the building to the site's existing ground cover further away. If the site is undeveloped land (bare earth, construction site), show the building as if the immediate plot has been developed (paved, landscaped) while the surrounding terrain beyond the plot boundary remains in its existing state. This is how real construction works — the building's plot is finished, the neighbours may not be.
Step 5 — Vegetation and material logic check. Before placing any vegetation or ground elements in the composition, verify logical plausibility: Trees and plants must only grow from soil, planting beds, or designated landscape areas — never from pavement, concrete, asphalt, rooftops (unless the building design includes rooftop gardens), or any hard surface. If the building's immediate ground plane is paved (plaza, parking, sidewalk), vegetation must be placed in cut-outs, planters, landscape strips, or adjacent unpaved areas — not growing through concrete. Street trees must be in tree grates or planting strips, not randomly emerging from road surfaces. The transition from the building's hardscaped zone to the site's natural vegetation should be gradual and logical — a clear edge between pavement and grass/dirt is natural; plants growing out of pavement is not. Existing site vegetation that falls within the building's footprint should be naturally displaced (as construction would require). Vegetation beyond the building's immediate plot should be preserved and may frame the composition.
Step 6 — Unify the two layers into one composition. The final image must not look like a building pasted onto a photograph. It must look like a single photograph taken on location with a professional camera. To achieve this: Match the building's lighting direction, shadow angles, and ambient colour temperature to the site's existing light conditions (or to the user-specified lighting override if provided). Apply atmospheric perspective — slight haze, colour desaturation, and blue-shift — to distant elements. If the building is in the mid-ground, it should show slight atmospheric effects consistent with its distance from the camera. Ground the building with contact shadows where it meets the terrain or pavement. For tall buildings, the contact shadow is substantial and extends across the ground plane realistically. The building's glass/reflective surfaces should reflect the site's environment — sky colour, surrounding vegetation, adjacent structures — not the original reflection from the building reference photo. Integrate the building's ground-level infrastructure (from Step 4) seamlessly with the site's terrain, creating a continuous ground plane with no visible seam or edge artifact between "building zone" and "site zone."
HIGH-QUALITY KEYWORDS — Maintain in every generation: photorealistic architectural site integration, hyper realism, shot on professional camera, natural shadows and volumetric lighting, unified 3D perspective, seamless layer blending, dimensional accuracy, realistic ground contact, atmospheric coherence, monumental building presence, architectural photography composition.
TEMPERATURE — 1 (default Gemini).
INPUT HIERARCHY — You will receive these inputs from the user, and you need to send them to Gemini API in one call, in the following importance hierarchy from one to thirteen:
1. siteImages (REQUIRED) — the site/land photograph uploaded by the user. This defines the terrain type, surroundings, vegetation, sky, light conditions, and general spatial character of the location. The site's overall character (terrain type, vegetation style, sky, distant features) must be preserved in the output. However, the camera framing, distance, and ground-level detail may be creatively adjusted to serve the building's scale and achieve a convincing architectural photograph. Localized modifications within and immediately around the building's footprint are encouraged where they serve realistic integration — including developing the immediate plot (paving, landscaping, infrastructure) as real construction would require.
2. referenceImages (REQUIRED) — the building/architectural reference image uploaded by the user. This defines the exact building to be placed on the site — its architectural form, façade design, massing, height, materials, and overall character. Preserve the building's design precisely — do not alter its architecture, materials, proportions, or design language. If the building reference has a different visual style from the site (e.g. CGI render vs raw photograph), always resolve the output toward hyper-realistic photographic quality — the building must adopt the photorealistic quality of a real structure, not retain its render aesthetic.
3. generationSettings.floors (Number of Floors) — if not '--', this specifies or confirms the number of floors the building should display. If the building reference shows a different floor count than what the user specified, honour the user's specification while maintaining the building's architectural language. Include as dimensional context for scale calculation.
4. generationSettings.lighting (Lighting Condition) — if not '--', this overrides the natural light interpretation from the site image. Options include: Dawn, Morning, Midday, Golden Hour, Dusk, Evening, Night, Overcast, Dramatic Cloudy. Apply the specified lighting condition to the entire unified composition — both site and building must share the same light direction, colour temperature, shadow intensity, and atmospheric quality. The lighting must feel natural and physically correct for the specified condition.
5. generationSettings.buildingType (Building Type) — if not '--', this provides architectural context: Residential, Commercial, Mixed-use, Hospitality. Use this to inform the building's ground-level treatment, entrance scale, surrounding landscaping approach, and site access integration.
6. generationSettings.surrounding (Surrounding Context) — if not '--', this defines the broader environmental context: Urban, Suburban, Coastal, Forest, Desert. Use this to inform atmospheric quality, vegetation type, ambient light character, and the visual relationship between the building and its surroundings. If the site image clearly shows a different context than selected, the site image takes priority — this setting provides supplementary atmospheric guidance only.
7. generationSettings.season (Season) — if not '--', this specifies: Summer, Winter, Autumn, Spring. Apply seasonal characteristics to the entire composition — vegetation colour and density, light quality, sky character, ground surface appearance. Both the site surroundings and the building's immediate landscaping must reflect the specified season consistently.
8. generationSettings.sky (Sky Condition) — if not '--', this specifies the sky treatment: Clear, Overcast, Golden hour, Dramatic clouds. Apply to the sky area of the composition. If both lighting and sky conditions are specified, they must be consistent with each other (e.g. Golden Hour lighting with Clear sky is coherent; Night lighting with Clear sky showing stars is coherent; Midday lighting with Dramatic clouds is coherent).
9. promptText — include the user's text literally, word for word. The user may provide specific integration instructions, camera angle preferences, landscaping requests, or atmospheric direction. The user may also attach additional reference images through the prompt field via libraryAssets — treat these as supplementary visual references for the generation.
10. commonAngleView — this toggle controls the primary camera angle of the generated image. If 'frontal': render the composition from a frontal/street-level perspective — the camera is positioned at human eye level (approximately 1.5–1.7 metres above ground), facing the building's primary façade. The building's front elevation, entrance, and vertical proportions should be clearly visible. If 'topdown': render as a TRUE ORTHOGRAPHIC TOP-DOWN VIEW — the camera is positioned directly above the site looking STRAIGHT DOWN at exactly 90 degrees perpendicular to the ground plane. This is NOT a bird's-eye view or aerial oblique angle. In a correct top-down view: ONLY the rooftop of the building, the site layout, roads, landscaping, and ground surfaces are visible. NO building façades, walls, windows, balconies, or vertical surfaces should be visible at all. The image should resemble a site plan, satellite photograph, or drone shot taken from directly overhead. The building appears as its roof footprint shape on the land. All perspective convergence is eliminated — the view is flat and orthographic.
11. selectedAngleShot — additional angle specification from the dropdown. If provided and not '--', this TAKES PRIORITY over commonAngleView and MUST be followed precisely. Each option defines a specific camera position: 'Front View' = camera at eye level (1.5–1.7m height), positioned directly in front of the building's main façade, looking straight at the entrance. The building's front elevation fills the frame. No significant vertical tilt. 'Side View' = camera at eye level, positioned at the lateral side of the building (perpendicular to the front façade), showing the building's side elevation, depth, and profile. The front façade is NOT the primary visible face. 'Top View' = TRUE ORTHOGRAPHIC TOP-DOWN VIEW — camera positioned directly overhead at exactly 90 degrees looking straight down at the ground. Only rooftops, site layout, roads, and ground surfaces visible. NO façades, NO walls, NO vertical surfaces visible. The image resembles a site plan or satellite view. This is the most strictly defined angle — any visible façade means the angle is wrong. 'Perspective View' = camera at eye level but positioned at a 30–45 degree horizontal angle to the building's front façade, showing both the front and one side of the building simultaneously. Natural perspective convergence with two vanishing points. This is the classic architectural perspective rendering angle. '3D View' = elevated camera positioned at approximately 30–45 degrees above the horizon (NOT directly overhead), angled to show the building's rooftop, front façade, and one side façade simultaneously — a three-quarter aerial perspective. This is similar to a drone photograph taken from moderate height at an oblique angle. Building façades ARE visible in this view, unlike Top View. The chosen camera angle must maintain correct 3D volume representation — the building's dimensional proportions, perspective foreshortening, and spatial depth must be geometrically accurate for the selected viewpoint.
12. selectedAspectRatio — extract ratio strictly from what the user selected in the UI. Pass that value exactly. Do not hardcode any dimensions.
13. selectedModel — use only to determine which image generation API to call. Do not pass the model name as text in the prompt.
STRICT RULES
Building Scale and Presence: The building must appear at its true dimensional scale in the composition. A 30-floor skyscraper must look like a 30-floor skyscraper — monumental, dominant, imposing — not like a scale model placed on a landscape photograph. If achieving truthful scale requires adjusting the camera distance, reframing the site composition, or showing the building closer than the original site photo's vantage point, make those adjustments. The building's monumental presence takes priority over preserving the exact camera position of the site photo. For high-rise buildings and skyscrapers: the building should occupy 60–80% of the image height. The viewer should feel as if they are standing near the building, looking up at it — not observing it from half a kilometre away. Use architectural photography composition: for tall buildings, the camera should be positioned 50–150 metres from the base, angled slightly upward, with perspective convergence visible in the upper floors. This is how real architectural photographers capture tall structures.
Site Character Preservation: The site's general character must be preserved — its terrain type, vegetation style, sky, distant features, and overall landscape identity should be recognisable in the output. However, the site is not a rigid container. The composition may be reframed, the camera distance adjusted, and the ground-level detail enriched to serve convincing integration. The goal is a believable architectural photograph on this site, not a pixel-perfect reproduction of the site photo with a building dropped in. Localized site modification within and immediately around the building's footprint is encouraged when it serves realistic integration. The building's immediate plot should look developed — paved, landscaped, with appropriate infrastructure — as real construction would require. Beyond the building's immediate perimeter, the site should transition back to its natural state.
Building Design Preservation: The building's architectural design, façade treatment, massing, proportions, and design language must be preserved faithfully. Do not alter the building's architecture. Minor adaptive adjustments are acceptable where they serve realistic site integration — such as adjusting the ground-floor treatment to respond to terrain grade, or adapting the building's base where it meets sloped land. These adjustments must not change the building's recognisable identity or design intent.
Ground-Level Integration: Every building must be grounded — its base must make physical contact with the terrain or a developed ground plane. Contact shadows must be present where the building meets the ground. Low-rise buildings: natural terrain contact with residential-scale landscaping is sufficient. Mid-rise buildings: require developed ground-level context — entrance area, pathways, transitional landscaping. High-rise buildings and skyscrapers: must have substantial ground-level infrastructure — podium, plaza, paved arrival area, entrance canopy, pedestrian-scale elements. A skyscraper sitting directly on raw dirt or grass is not acceptable. If the site is undeveloped land, the building's immediate plot must be shown as developed (as if construction of the building and its surroundings is complete) while the broader site retains its existing character. If the terrain is sloped, the building's foundation must respond to the grade realistically (stepped foundation, terraced base, or cut-and-fill platform). No floating buildings.
Vegetation Logic: Vegetation must only grow from soil, planting beds, tree grates, or designated landscape areas — never from pavement, concrete, asphalt, or any impervious hard surface. If the building's ground plane includes paved areas (plaza, sidewalk, parking), any trees or plants must be in proper planting infrastructure — cut-outs in pavement, raised planters, landscape strips between paved zones. Random plants growing through concrete is a visual error that must be avoided. Street trees along roads must be in tree grates or planting strips, not emerging from the road surface. The transition from the building's developed zone to the site's natural terrain should be gradual and logically constructed — not an abrupt edge where pavement suddenly becomes wilderness.
Photorealistic Unification: The output must be a single unified photorealistic composition — not two layers composited together. The building and site must share the same light direction, shadow behaviour, atmospheric perspective, colour temperature, and camera perspective. If the building reference and site image have different visual styles (e.g. CGI render placed on raw photograph), always resolve toward hyper-realistic photographic quality. The building must look like a real physical structure photographed on location. There must be no visible boundary, edge artifact, or stylistic discontinuity between the building and the site. The building's reflective surfaces (glass, polished metal) should reflect the site's environment — not the original reflections from the building reference image. Camera angle and 3D volume must be harmonized. The building's perspective vanishing points must match the composition's camera position. Mismatched perspective is the primary cause of unconvincing composites.
Dimensional Accuracy: The building's scale must correspond logically to visible scale markers (trees are typically 8–15 metres tall, single-storey structures are 3–4 metres, vehicles are approximately 4.5 metres long, human figures are approximately 1.7 metres). The building must not appear oversized or undersized relative to its surroundings. If the only way to achieve correct scale within the site photo's original framing would make the building appear too small, reframe the composition to a closer viewpoint.
General Rules: Do not use any style labels unless the user explicitly wrote them in promptText. If any input is missing or '--', skip it silently without generating filler content. No explanations, no formatting, no headings in output.`;

      const currentSelectedModel = params?.selectedModel || 'Gemini 3.1 Flash Image Preview';
      const promptText = params?.promptText || '';
      const commonAngleView = params?.commonAngleView || 'frontal';
      const selectedAngleShot = params?.selectedAngleShot || '--';
      const selectedAspectRatio = params?.selectedAspectRatio || '1:1';

      // Build the full unified prompt (baseInstruction + system prompt + generation settings)
      const baseInstruction =
        "Generate exactly what the prompt describes. Integrate the reference building onto the reference site seamlessly. " +
        "The final composition must be a unified, photorealistic image where the building appears naturally placed on the site.";

      let fullPrompt = `${baseInstruction}\n\n${siteConceptSystemPrompt}`;

      // Add generation settings
      const settingsParts = [];
      if (generationSettings?.floors && generationSettings.floors !== '--' && generationSettings.floors !== '') {
        settingsParts.push(`NUMBER OF FLOORS: ${generationSettings.floors}`);
      }
      if (generationSettings?.lighting && generationSettings.lighting !== '--' && generationSettings.lighting !== '') {
        settingsParts.push(`LIGHTING CONDITION: ${generationSettings.lighting}`);
      }
      if (generationSettings?.buildingType && generationSettings.buildingType !== '--' && generationSettings.buildingType !== '') {
        settingsParts.push(`BUILDING TYPE: ${generationSettings.buildingType}`);
      }
      if (generationSettings?.surrounding && generationSettings.surrounding !== '--' && generationSettings.surrounding !== '') {
        settingsParts.push(`SURROUNDING CONTEXT: ${generationSettings.surrounding}`);
      }
      if (generationSettings?.season && generationSettings.season !== '--' && generationSettings.season !== '') {
        settingsParts.push(`SEASON: ${generationSettings.season}`);
      }
      if (generationSettings?.sky && generationSettings.sky !== '--' && generationSettings.sky !== '') {
        settingsParts.push(`SKY CONDITION: ${generationSettings.sky}`);
      }
      if (promptText?.trim()) {
        settingsParts.push(`USER PROMPT TEXT: ${promptText.trim()}`);
      }
      if (commonAngleView && commonAngleView !== '--') {
        const angleDesc = commonAngleView === 'topdown'
          ? 'topdown — ORTHOGRAPHIC TOP-DOWN: camera directly above at 90 degrees looking straight down. Only rooftops and ground visible. NO facades, NO walls, NO vertical surfaces.'
          : 'frontal — street-level perspective at eye height (1.5–1.7m), facing the building front facade';
        settingsParts.push(`COMMON ANGLE VIEW: ${angleDesc}`);
      }
      if (selectedAngleShot && selectedAngleShot !== '--' && selectedAngleShot !== 'Angle view') {
        const angleShotDescriptions = {
          'Front View': 'Front View — camera at eye level, directly facing the main facade',
          'Side View': 'Side View — camera at eye level, perpendicular to the front facade, showing the side elevation',
          'Top View': 'Top View — TRUE ORTHOGRAPHIC TOP-DOWN: camera directly overhead at exactly 90 degrees looking straight down. ONLY rooftops, roads, landscaping visible. NO facades, NO walls, NO windows, NO vertical surfaces at all. Resembles a site plan or satellite photograph.',
          'Perspective View': 'Perspective View — camera at eye level, 30-45 degree angle to facade, showing front and one side simultaneously',
          '3D View': '3D View — elevated camera at 30-45 degrees above horizon (NOT directly overhead), showing rooftop plus facades. Three-quarter aerial perspective.',
        };
        settingsParts.push(`SELECTED ANGLE SHOT (PRIORITY — overrides common angle): ${angleShotDescriptions[selectedAngleShot] || selectedAngleShot}`);
      }
      if (selectedAspectRatio && selectedAspectRatio !== '--') {
        settingsParts.push(`ASPECT RATIO: ${selectedAspectRatio} (${dimensions.width}x${dimensions.height})`);
      }
      if (settingsParts.length > 0) {
        fullPrompt += `\n\nGENERATION SETTINGS:\n${settingsParts.join('\n')}`;
      }
      fullPrompt += `\n\nCRITICAL: Generate output image in EXACT dimensions: ${dimensions.width}x${dimensions.height} pixels.`;

      let generatedImageUrl;

      if (isFluxModel(currentSelectedModel)) {
        generatedImageUrl = await generateWithFlux({
          modelName: currentSelectedModel,
          directPromptText: fullPrompt,
          userPromptText: promptText,
          siteImages: siteImages,
          referenceImages: referenceImages,
          generationSettings: generationSettings,
          toolSystemPrompt: fullPrompt,
          commonAngleView: commonAngleView,
          selectedAngleShot: selectedAngleShot,
          libraryAssets: params?.libraryAssets || [],
          imageSize: selectedAspectRatio,
          signal: abortControllerRef.current.signal,
        });
      } else {
        generatedImageUrl = await generateSiteConcept({
          siteImages: siteImages,
          referenceImages: referenceImages,
          generationSettings: generationSettings,
          promptText: promptText,
          commonAngleView: commonAngleView,
          selectedAngleShot: selectedAngleShot,
          selectedAspectRatio: selectedAspectRatio,
          selectedModel: currentSelectedModel,
          dimensions: dimensions,
          toolSystemPrompt: fullPrompt,
          libraryAssets: params?.libraryAssets || [],
          signal: abortControllerRef.current.signal,
        });
      }

      // Resize image to correct dimensions
      const resizedImageUrl = await resizeImageToCorrectDimensions(generatedImageUrl, dimensions);
      // Revoke previous generated image blob URL before replacing
      if (generatedImage && generatedImage.startsWith('blob:')) {
        URL.revokeObjectURL(generatedImage);
      }
      setGeneratedImage(resizedImageUrl);
      setLibraryAssets((prev) => [...prev, resizedImageUrl]);
      setSessionLibraryImages((prev) => [...prev, resizedImageUrl]);
      clearTimer();
      setIsGenerating(false);
      setGenerationError('');
      toast.success('Site concept generated successfully!');
      showSaveModal(resizedImageUrl, location.pathname);

      // Save to database
      const { data: { session } } = await supabase?.auth?.getSession();
      const currentUser = session?.user;
      await saveGenerationToDatabase(resizedImageUrl, params?.promptText || '', currentUser);
      // Deduct credits
      await userProfilesService?.updateCredits(currentUser?.id, getModelCreditCost(params?.selectedModel || 'Gemini 3.1 Flash Image Preview'));

    } catch (error) {
      if (error?.name === 'AbortError' || error?.message?.includes('cancelled')) {
        setCancelled();
      } else {
        console.error('Error generating site concept:', error);
        setFailed();
        setGenerationError(error?.message || 'Failed to generate site concept');
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
      isGeneratingRef.current = false;
    }
  };

  const handleCancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setCancelled();
      setIsGenerating(false);
    }
  };

  const saveGenerationToDatabase = async (imageUrl, prompt, currentUser = null) => {
    const activeUser = currentUser || user;
    if (!activeUser) return;

    try {
      // Upload image to Supabase Storage (convert base64 → public URL)
      const storageUrl = await storageService.uploadImage(imageUrl, activeUser?.id);
      imageUrl = storageUrl;

      let projectId = currentProjectId;
      if (!projectId) {
        const projectName = `Site Concept - ${new Date()?.toLocaleDateString()}`;
        const { data: project, error: projectError } = await projectsService?.createProject(activeUser?.id, {
          name: projectName,
          description: prompt,
          thumbnail: imageUrl,
          toolType: 'site-concept',
          toolName: 'Site Concept',
          status: 'completed',
          tags: [generationSettings?.model || 'Gemini']?.filter(Boolean),
          variations: 1,
        });
        if (projectError) { console.error('Error creating project:', projectError); return; }
        projectId = project?.id;
        setCurrentProjectId(projectId);
      } else {
        await projectsService?.incrementVariations(projectId);
        await projectsService?.updateProject(projectId, { thumbnail: imageUrl });
      }

      const { data: design, error: designError } = await savedDesignsService?.createDesign(activeUser?.id, projectId, {
        imageUrl,
        prompt,
        isFavorite: false,
      });
      if (designError) { console.error('Error saving design:', designError); return; }

      await generationMetadataService?.createMetadata(activeUser?.id, design?.id, {
        modelUsed: generationSettings?.model || 'Gemini',
        imageSize: generationSettings?.imageSize || 'Auto',
        style: 'Site Concept',
        resolution: generationSettings?.imageSize || 'Auto',
        lighting: generationSettings?.lighting || 'Auto',
        quality: 'High',
        usedMedia: [...siteImages, ...referenceImages],
        additionalParams: {
          prompt,
          floors: generationSettings?.floors || '',
          buildingType: generationSettings?.buildingType || '',
          surrounding: generationSettings?.surrounding || '',
          season: generationSettings?.season || '',
          sky: generationSettings?.sky || '',
        },
      });
    } catch (error) {
      console.error('Error saving to database:', error);
    }
  };

  return (
    <>
      <GenerationStatusNotification status={generationStatus} onDismiss={dismissNotification} />
      <Helmet>
        <title>Site Concept - Roomwork.ai</title>
        <meta name="description" content="Site concept planning and visualization tool" />
      </Helmet>
      <div className="min-h-screen bg-black overflow-x-hidden">
        <Header />
        <main className="pt-20 lg:pt-24 px-4 pb-[20vh]">
          <div className="max-w-[1400px] mx-auto">
            {/* Desktop Header — aligned to content columns */}
            <div className="hidden md:flex gap-4 justify-center mb-6">
              <div className="w-[400px] flex-shrink-0">
                <button
                  onClick={() => navigate('/main-dashboard')}
                  className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4">
                  <Icon name="ArrowLeft" size={20} />
                  <span className="text-sm font-medium">Back to Dashboard</span>
                </button>
                <h1 className="text-3xl font-bold text-white">Site Concept</h1>
              </div>
              <div className="flex-1 max-w-[900px] flex items-end justify-end gap-3 flex-shrink min-w-0">
                <button
                  onClick={openGuide}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-all flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.4)', boxShadow: '0 4px 16px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)' }}
                >
                  <Icon name="HelpCircle" size={18} />
                  <span className="hidden lg:inline">Virtual Tool Guide</span>
                </button>
                <button
                  onClick={() => navigate('/project-history')}
                  className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform flex-shrink-0">
                  <Icon name="History" size={18} />
                  <span className="hidden lg:inline">View Projects</span>
                </button>
              </div>
            </div>

            {/* Mobile Header */}
            <div className="md:hidden mb-6">
              <button
                onClick={() => navigate('/main-dashboard')}
                className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4">
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>
              <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-white">Site Concept</h1>
                <div className="flex items-center gap-2">
                  <button
                    onClick={openGuide}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-white text-xs font-medium hover:scale-[1.02] transition-all whitespace-nowrap flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.4)', boxShadow: '0 4px 16px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)' }}
                  >
                    <Icon name="HelpCircle" size={15} />
                    Guide
                  </button>
                  <button
                    onClick={() => navigate('/project-history')}
                    className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform flex-shrink-0">
                    <Icon name="History" size={18} />
                    <span className="hidden md:inline">View Projects</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop/Tablet Layout */}
            <div className="hidden md:flex gap-4 justify-center">
              {/* Left Panel - Upload Windows (First Third) */}
              <div className="w-[400px] flex-shrink-0 space-y-4">
                {/* Site Image Upload */}
                <div className="text-left mb-2">
                  <span className={`text-sm font-medium ${totalUploads >= 6 ? "text-red-400" : "text-white/70"}`}>
                    {totalUploads} / 6 images uploaded
                  </span>
                </div>
                <div
                  ref={siteUploadDesktopRef}
                  className="glassmorphic-card rounded-2xl p-3 border border-transparent transition-all duration-200"
                  style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  onDragOver={handleUploadDragOver}
                  onDragLeave={handleUploadDragLeave}
                  onDrop={handleSiteDrop}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white text-sm font-medium">
                      Site Image<span className="text-red-500">*</span>
                    </h3>
                  </div>

                  <label className={`block ${totalUploads >= 6 ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg"
                      onChange={handleSiteUpload}
                      className="hidden"
                      disabled={totalUploads >= 6}
                    />
                    <div className="glassmorphic-upload-zone rounded-2xl p-7 text-center border-2 border-dashed border-white/20 hover:border-orange-500/40 transition-all duration-300">
                      <Icon name="Image" size={36} className="text-orange-500 mx-auto mb-2.5" />
                      <p className="text-white/70 text-sm">Upload image PNG or JPG</p>
                    </div>
                  </label>
                  {siteImages.length > 0 && (
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      {siteImages.map((img, idx) => (
                        <UploadedImageThumb
                          key={idx}
                          src={img}
                          alt={`Site ${idx + 1}`}
                          onRemove={() => handleRemoveSiteImage(idx)}
                          imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"
                          height="h-auto"
                          previewWidth="w-[200px]"
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Reference Building Upload */}
                <div
                  ref={refBuildingDesktopRef}
                  className="glassmorphic-card rounded-2xl p-5 border border-transparent transition-all duration-200"
                  style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  onDragOver={handleUploadDragOver}
                  onDragLeave={handleUploadDragLeave}
                  onDrop={handleReferenceDrop}
                >
                  <h3 className="text-white text-sm font-medium mb-3.5">
                    Reference Building<span className="text-red-500">*</span>
                  </h3>

                  <label className={`block ${totalUploads >= 6 ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg"
                      onChange={handleReferenceUpload}
                      className="hidden"
                      disabled={totalUploads >= 6}
                    />
                    <div className="glassmorphic-upload-zone rounded-2xl p-7 text-center border-2 border-dashed border-white/20 hover:border-orange-500/40 transition-all duration-300">
                      <Icon name="Image" size={36} className="text-orange-500 mx-auto mb-2.5" />
                      <p className="text-white/70 text-sm">Upload image PNG or JPG</p>
                    </div>
                  </label>
                  {referenceImages.length > 0 && (
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      {referenceImages.map((img, idx) => (
                        <UploadedImageThumb
                          key={idx}
                          src={img}
                          alt={`Reference ${idx + 1}`}
                          onRemove={() => handleRemoveReferenceImage(idx)}
                          imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"
                          height="h-auto"
                          previewWidth="w-[200px]"
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Generation Settings - Desktop */}
                <div
                  ref={genSettingsDesktopRef}
                  style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined }}
                >
                  <GenerationSettings
                    settings={generationSettings}
                    onSettingsChange={setGenerationSettings}
                  />
                </div>

                {/* Asset Library */}
                <div
                  ref={assetLibraryDesktopRef}
                  style={{ borderRadius: '1.5rem', boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined }}
                >
                  <AssetLibraryPanel
                    libraryAssets={libraryAssets}
                    setLibraryAssets={setLibraryAssets}
                    showDragHint={true}
                    generatedImages={sessionLibraryImages}
                    setGeneratedImages={setSessionLibraryImages}
                    currentProjectId={currentProjectId}
                    onImageDeleted={(remaining) => {
                      if (!isGenerating) setGeneratedImage(remaining[0] || null);
                    }}
                  />
                </div>
              </div>

              {/* Center+Right Panel - Result Window (Two Thirds) */}
              <div className="flex-1 max-w-[900px] space-y-4">
                <div
                  ref={resultDesktopRef}
                  className="glassmorphic-card rounded-3xl p-4 relative"
                  style={{ boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined }}
                >
                  <div className="flex justify-end items-center mb-3 px-2">
                    <span className="text-white/70 text-sm">Result</span>
                  </div>
                  <div className="relative rounded-2xl overflow-hidden bg-black/40 h-[550px]">
                    {isGenerating ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <Icon name="Loader" size={48} className="text-orange-500 mx-auto mb-3 animate-spin" />
                          <p className="text-white text-sm font-medium">Generating your design...</p>
                          <p className="text-white/60 text-xs mt-1">This may take a moment</p>
                        </div>
                      </div>
                    ) : generatedImage ? (
                      <img
                        src={generatedImage}
                        alt="Generated site concept"
                        className="w-full h-full object-contain cursor-grab active:cursor-grabbing"
                        draggable
                        onDragStart={(e) => setGeneratedImageDragData(e, generatedImage)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <Icon name="Image" size={48} className="text-white/30 mx-auto mb-3" />
                          <p className="text-white/50 text-sm">No image generated yet</p>
                          <p className="text-white/30 text-xs mt-1">Upload images and click Generate</p>
                        </div>
                      </div>
                    )}

                    {/* Error message overlay */}
                    {generationError && !isGenerating && (
                      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                        <div className="text-center max-w-md px-4">
                          <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-3" />
                          <p className="text-white text-sm font-medium mb-2">Generation Failed</p>
                          <p className="text-white/70 text-xs mb-4">{generationError}</p>
                          <button
                            onClick={() => setGenerationError('')}
                            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-medium transition-all"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="absolute bottom-4 right-4 flex items-center space-x-2">
                      <button
                        onClick={handleDownload}
                        disabled={!generatedImage || isGenerating}
                        className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Download"
                      >
                        <Icon name="Download" size={18} className="text-white" />
                      </button>
                      <button
                        onClick={handleDeleteResult}
                        disabled={!generatedImage || isGenerating}
                        className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete"
                      >
                        <Icon name="Trash2" size={18} className="text-white" />
                      </button>
                      <button
                        onClick={handleRegenerate}
                        disabled={!generatedImage || isGenerating}
                        className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Regenerate"
                      >
                        <Icon name="RefreshCw" size={18} className="text-white" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Prompt Task Bar - Desktop */}
                <PromptTaskBar
                  isGenerating={isGenerating}
                  onGenerate={(params) => {
                    setLastGenerationParams(params);
                    handleGenerate(params);
                  }}
                  onCancel={handleCancelGeneration}
                  disabled={siteImages.length === 0 || referenceImages.length === 0}
                  libraryAssets={libraryAssets}
                  setLibraryAssets={setLibraryAssets}
                  initialPrompt={restoredPrompt}
                  initialModel={restoredModel}
                  initialImageSize={restoredImageSize}
                  promptGuideRef={promptDesktopRef}
                  angleGuideRef={angleDesktopRef}
                  modelGuideRef={modelDesktopRef}
                  aspectRatioGuideRef={aspectRatioGuideDesktopRef}
                  resolutionGuideRef={resolutionGuideDesktopRef}
                  generateGuideRef={generateBtnDesktopRef}
                  activeGuideStep={guideStep}
                  showGuide={showGuide}
                  guideVisible={guideVisible}
                />
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="md:hidden space-y-4">
              {/* Result Window */}
              <div
                ref={resultMobileRef}
                className="glassmorphic-card rounded-2xl p-3 relative"
                style={{ boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined }}
              >
                <div className="flex justify-end items-center mb-2 px-1">
                  <span className="text-white/70 text-xs">Result</span>
                </div>
                <div className="relative rounded-2xl overflow-hidden bg-black/40 h-[280px]">
                  {isGenerating ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Loader" size={36} className="text-orange-500 mx-auto mb-2 animate-spin" />
                        <p className="text-white text-xs font-medium">Generating...</p>
                      </div>
                    </div>
                  ) : generatedImage ? (
                    <img
                      src={generatedImage}
                      alt="Generated site concept"
                      className="w-full h-full object-contain cursor-grab active:cursor-grabbing"
                      draggable
                      onDragStart={(e) => setGeneratedImageDragData(e, generatedImage)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Image" size={36} className="text-white/30 mx-auto mb-2" />
                        <p className="text-white/50 text-xs">No image generated yet</p>
                        <p className="text-white/30 text-[10px] mt-1">Upload images and click Generate</p>
                      </div>
                    </div>
                  )}

                  <div className="absolute bottom-3 right-3 flex gap-1.5">
                    <button
                      onClick={handleDownload}
                      disabled={!generatedImage}
                      className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download"
                    >
                      <Icon name="Download" size={14} className="text-white" />
                    </button>
                    <button
                      onClick={handleDeleteResult}
                      disabled={!generatedImage}
                      className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={14} className="text-white" />
                    </button>
                    <button
                      onClick={handleRegenerate}
                      disabled={!generatedImage || isGenerating}
                      className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Regenerate"
                    >
                      <Icon name="RefreshCw" size={14} className="text-white" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Upload Windows Side by Side */}
              <div className="grid grid-cols-1 gap-3">
                <div className="text-left mb-2">
                  <span className={`text-sm font-medium ${totalUploads >= 6 ? "text-red-400" : "text-white/70"}`}>
                    {totalUploads} / 6 images uploaded
                  </span>
                </div>
                {/* Site Image Upload */}
                <div
                  ref={siteUploadMobileRef}
                  className="glassmorphic-card rounded-2xl p-3 border border-transparent transition-all duration-200"
                  style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  onDragOver={handleUploadDragOver}
                  onDragLeave={handleUploadDragLeave}
                  onDrop={handleSiteDrop}
                >
                  <h3 className="text-white text-xs font-medium mb-2">
                    Site Image<span className="text-red-500">*</span>
                  </h3>
                  <label className={`block ${totalUploads >= 6 ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg"
                      onChange={handleSiteUpload}
                      className="hidden"
                      disabled={totalUploads >= 6}
                    />
                    <div className="glassmorphic-upload-zone rounded-xl p-4 text-center border-2 border-dashed border-white/20 hover:border-orange-500/40 transition-all duration-300">
                      <Icon name="Image" size={24} className="text-orange-500 mx-auto mb-1.5" />
                      <p className="text-white/70 text-[10px]">Upload PNG or JPG</p>
                    </div>
                  </label>
                  {siteImages.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {siteImages.map((img, idx) => (
                        <UploadedImageThumb
                          key={idx}
                          src={img}
                          alt={`Site ${idx + 1}`}
                          onRemove={() => handleRemoveSiteImage(idx)}
                          imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10"
                          height="h-auto"
                          previewWidth="w-[200px]"
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Reference Building Upload */}
                <div
                  ref={refBuildingMobileRef}
                  className="glassmorphic-card rounded-2xl p-3 border border-transparent transition-all duration-200"
                  style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  onDragOver={handleUploadDragOver}
                  onDragLeave={handleUploadDragLeave}
                  onDrop={handleReferenceDrop}
                >
                  <h3 className="text-white text-xs font-medium mb-2">
                    Reference Building<span className="text-red-500">*</span>
                  </h3>
                  <label className={`block ${totalUploads >= 6 ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg"
                      onChange={handleReferenceUpload}
                      className="hidden"
                      disabled={totalUploads >= 6}
                    />
                    <div className="glassmorphic-upload-zone rounded-xl p-4 text-center border-2 border-dashed border-white/20 hover:border-orange-500/40 transition-all duration-300">
                      <Icon name="Image" size={24} className="text-orange-500 mx-auto mb-1.5" />
                      <p className="text-white/70 text-[10px]">Upload PNG or JPG</p>
                    </div>
                  </label>
                  {referenceImages.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {referenceImages.map((img, idx) => (
                        <UploadedImageThumb
                          key={idx}
                          src={img}
                          alt={`Reference ${idx + 1}`}
                          onRemove={() => handleRemoveReferenceImage(idx)}
                          imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10"
                          height="h-auto"
                          previewWidth="w-[200px]"
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Asset Library - Mobile */}
              <div
                ref={assetLibraryMobileRef}
                style={{ borderRadius: '1.5rem', boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined }}
              >
                <AssetLibraryPanel
                  libraryAssets={libraryAssets}
                  setLibraryAssets={setLibraryAssets}
                  showDragHint={true}
                  generatedImages={sessionLibraryImages}
                  setGeneratedImages={setSessionLibraryImages}
                  currentProjectId={currentProjectId}
                  onImageDeleted={(remaining) => {
                    if (!isGenerating) setGeneratedImage(remaining[0] || null);
                  }}
                />
              </div>

              {/* Generation Settings - Mobile */}
              <div
                ref={genSettingsMobileRef}
                style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined }}
              >
                <GenerationSettingsMobile
                  settings={generationSettings}
                  onSettingsChange={setGenerationSettings}
                />
              </div>

              {/* Prompt Task Bar - Mobile */}
              <PromptTaskBarMobile
                isGenerating={isGenerating}
                onGenerate={(params) => {
                  setLastGenerationParams(params);
                  handleGenerate(params);
                }}
                onCancel={handleCancelGeneration}
                disabled={siteImages.length === 0 || referenceImages.length === 0}
                libraryAssets={libraryAssets}
                setLibraryAssets={setLibraryAssets}
                initialPrompt={restoredPrompt}
                initialModel={restoredModel}
                initialImageSize={restoredImageSize}
                promptGuideRef={promptMobileRef}
                angleGuideRef={angleMobileRef}
                modelGuideRef={modelMobileRef}
                aspectRatioGuideRef={aspectRatioGuideMobileRef}
                resolutionGuideRef={resolutionGuideMobileRef}
                generateGuideRef={generateBtnMobileRef}
                activeGuideStep={guideStep}
                showGuide={showGuide}
                guideVisible={guideVisible}
              />
            </div>

          </div>
        </main>
      </div>
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)'
          }
        }}
      />

      {/* Virtual Tool Guide Overlay */}
      {showGuide && (
        <>
          <div className="fixed inset-0 z-[200]" onClick={closeGuide}>
            <div
              className="absolute inset-0 transition-opacity duration-600 ease-[cubic-bezier(0.4,0,0.2,1)]"
              style={{ background: 'rgba(0, 0, 0, 0.30)', opacity: guideVisible ? 1 : 0 }}
            />
          </div>

          {/* Tooltip bubble */}
          <div
            className="fixed z-[9999] w-[280px] md:w-[300px] pointer-events-none"
            style={(() => {
              const desktopRefsList = [siteUploadDesktopRef.current, refBuildingDesktopRef.current, genSettingsDesktopRef.current, assetLibraryDesktopRef.current, promptDesktopRef.current, angleDesktopRef.current, modelDesktopRef.current, aspectRatioGuideDesktopRef.current, resolutionGuideDesktopRef.current, generateBtnDesktopRef.current, resultDesktopRef.current];
              const mobileRefsList = [siteUploadMobileRef.current, refBuildingMobileRef.current, genSettingsMobileRef.current, assetLibraryMobileRef.current, promptMobileRef.current, angleMobileRef.current, modelMobileRef.current, aspectRatioGuideMobileRef.current, resolutionGuideMobileRef.current, generateBtnMobileRef.current, resultMobileRef.current];
              const isDesktop = window.innerWidth >= 768;
              const ref = isDesktop ? desktopRefsList[guideStep - 1] : mobileRefsList[guideStep - 1];
              const bubbleWidth = isDesktop ? 300 : 280;
              if (!ref) return { top: '50%', left: '50%' };
              const rect = ref.getBoundingClientRect();
              // Steps 1-4 are on the left panel → bubble appears to the right
              // Steps 5-9 are on the right panel → bubble appears to the left
              if (!isDesktop) {
                return {
                  top: rect.bottom + 12,
                  left: Math.max(12, Math.min(window.innerWidth - bubbleWidth - 12, rect.left + rect.width / 2 - bubbleWidth / 2)),
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0px)' : 'translateY(-8px)',
                  transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                };
              }
              if (guideStep >= 5) {
                return {
                  top: rect.top + rect.height / 2,
                  left: rect.left - bubbleWidth - 32,
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(24px)',
                  transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                };
              }
              return {
                top: rect.top + rect.height / 2,
                left: rect.right + 32,
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(-24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            })()}
          >
            <div className="pointer-events-auto">
              {/* Arrow indicator */}
              {window.innerWidth >= 768 && guideStep >= 5 ? (
                <>
                  <div
                    className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderLeft: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute top-1/2 -right-[6px] -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderLeft: '9px solid rgba(30, 30, 30, 0.95)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              ) : window.innerWidth >= 768 ? (
                <>
                  <div
                    className="absolute top-1/2 -left-2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderRight: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute top-1/2 -left-[6px] -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderRight: '9px solid rgba(30, 30, 30, 0.95)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              ) : null}

              <div
                className="rounded-2xl p-5 text-center"
                style={{ background: 'rgb(24, 24, 24)', border: '1px solid rgba(254, 131, 56, 0.3)', boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(254, 131, 56, 0.1)' }}
              >
                <div
                  className="flex items-center justify-center gap-2 mb-2"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s' }}
                >
                  <Icon name={[null, 'Image', 'Building2', 'Settings2', 'FolderOpen', 'MessageSquare', 'Navigation', 'Cpu', 'RectangleHorizontal', 'ScanLine', 'Sparkles', 'Monitor'][guideStep] || 'Settings'} size={18} className="text-white/50" />
                  <span className="text-sm font-semibold text-white">Step {guideStep}</span>
                </div>
                <p
                  className="text-sm text-white/80 leading-relaxed"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s' }}
                >
                  {guideStep === 1 ? (
                    <>Upload your <span className="text-white font-medium italic">Site Image</span> — a photo of the location where the building will be placed. PNG or JPG format. This is required to start generation.</>
                  ) : guideStep === 2 ? (
                    <>Upload <span className="text-white font-medium italic">Reference Building</span> images that define the architectural style you want to achieve. This also required.</>
                  ) : guideStep === 3 ? (
                    <>Configure <span className="text-white font-medium italic">Generation Settings</span> — set the number of floors, lighting condition, building type, surrounding context, season, and sky conditions to guide the AI.</>
                  ) : guideStep === 4 ? (
                    <>Your saved and generated images are stored in the <span className="text-white font-medium italic">Asset Library</span>. Drag and drop them into the prompt area or upload zones to reuse them.</>
                  ) : guideStep === 5 ? (
                    <>Add optional <span className="text-white font-medium italic">text instructions</span> to refine the generated design. You can also drag and drop additional reference images directly into this area.</>
                  ) : guideStep === 6 ? (
                    <>Select the <span className="text-white font-medium italic">camera angle</span> for your site concept — choose between a frontal view, top-down aerial view shortcut toggle button, or a custom angle using the dropdown.</>
                  ) : guideStep === 7 ? (
                    <>Image generating <span className="text-white font-medium italic">Models</span> which create images. Different models allow you to explore different results.</>
                  ) : guideStep === 8 ? (
                    <>Image ratio: <span className="text-white font-medium italic">vertical or horizontal</span> (choose the precise ratio according to the dropdown menu icons). Different ratios allow you to explore different room compositions.</>
                  ) : guideStep === 9 ? (
                    <>A dropdown selector that allows you to choose the output image <span className="text-white font-medium italic">resolution</span> for your generation. Higher resolution produces a more detailed and sharper image, suitable for professional presentations or print use.</>
                  ) : guideStep === 10 ? (
                    <>Hit <span className="text-white font-medium italic">'Generate Image'</span> once you're ready. Make sure both a site image and a reference building are uploaded before generating.</>
                  ) : guideStep === 11 ? (
                    <>The AI-generated site concept will appear in the <span className="text-white font-medium italic">Result</span> window. Use the buttons to <span className="text-white font-medium italic">download</span>, <span className="text-white font-medium italic">delete</span>, or <span className="text-white font-medium italic">regenerate</span> with the same settings.</>
                  ) : null}
                </p>
                <button
                  onClick={() => guideStep < guideStepCount ? nextGuideStep() : closeGuide()}
                  className="mt-4 px-5 py-2 glassmorphic-button rounded-xl text-white text-xs font-medium hover:scale-[1.02]"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s' }}
                >
                  {guideStep < guideStepCount ? 'Next' : 'Got it'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SiteConcept;
